import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLanguage } from '../../features/language/useLanguage';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 15, 25, 50];

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  siblingCount?: number;
  boundaryCount?: number;
  isLoading?: boolean;
  /** Tight layout for narrow containers (e.g. side-by-side cards): icon-only
   *  prev/next, fewer page buttons, smaller controls, wraps instead of overflowing. */
  compact?: boolean;
  className?: string;
}

type PageItem = number | 'ellipsis-left' | 'ellipsis-right';

function buildPageItems(
  currentPage: number,
  totalPages: number,
  siblingCount: number,
  boundaryCount: number
): PageItem[] {
  if (totalPages <= 1) return [1];

  const startPages = Array.from({ length: Math.min(boundaryCount, totalPages) }, (_, i) => i + 1);
  const endPages = Array.from(
    { length: Math.min(boundaryCount, totalPages) },
    (_, i) => totalPages - boundaryCount + i + 1
  ).filter(p => p > boundaryCount);

  const siblingsStart = Math.max(currentPage - siblingCount, boundaryCount + 1);
  const siblingsEnd = Math.min(currentPage + siblingCount, totalPages - boundaryCount);

  const items: PageItem[] = [...startPages];

  if (siblingsStart > boundaryCount + 1) {
    items.push('ellipsis-left');
  } else {
    for (let p = boundaryCount + 1; p < siblingsStart; p++) items.push(p);
  }

  for (let p = siblingsStart; p <= siblingsEnd; p++) {
    if (!items.includes(p) && p > 0 && p <= totalPages) items.push(p);
  }

  if (siblingsEnd < totalPages - boundaryCount) {
    items.push('ellipsis-right');
  } else {
    for (let p = siblingsEnd + 1; p <= totalPages - boundaryCount; p++) {
      if (!items.includes(p) && p > 0 && p <= totalPages) items.push(p);
    }
  }

  endPages.forEach(p => {
    if (!items.includes(p)) items.push(p);
  });

  return items;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  siblingCount = 1,
  boundaryCount = 1,
  isLoading = false,
  compact = false,
  className,
}) => {
  const { t } = useLanguage();

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const safePage = Math.min(Math.max(1, page), totalPages);

  // Compact keeps the page list short so it never outgrows a narrow card.
  const effSiblingCount = compact ? 0 : siblingCount;
  const effBoundaryCount = compact ? 1 : boundaryCount;

  const items = useMemo(
    () => buildPageItems(safePage, totalPages, effSiblingCount, effBoundaryCount),
    [safePage, totalPages, effSiblingCount, effBoundaryCount]
  );

  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);

  const prevDisabled = safePage <= 1 || isLoading;
  const nextDisabled = safePage >= totalPages || isLoading;

  const baseBtn = cn(
    'inline-flex items-center justify-center px-2 text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
    compact ? 'min-w-[2rem] h-8' : 'min-w-[2.25rem] h-9'
  );

  return (
    <nav
      className={cn(
        compact
          ? 'flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 py-2.5 bg-white border-t border-gray-200'
          : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 bg-white border-t border-gray-200',
        isLoading && 'opacity-70',
        className
      )}
      aria-label={t('common.pagination.label')}
      data-test-id="pagination"
    >
      <div
        className={cn(
          'flex items-center gap-2 text-sm text-gray-600',
          compact ? 'min-w-0' : 'flex-col sm:flex-row order-2 sm:order-1'
        )}
      >
        <span data-test-id="pagination-showing" className={compact ? 'whitespace-nowrap' : undefined}>
          {total === 0 ? (
            t('common.pagination.empty')
          ) : compact ? (
            t('common.pagination.showing', { from, to, total })
          ) : (
            <>
              <span className="hidden sm:inline">
                {t('common.pagination.showing', { from, to, total })}
              </span>
              <span className="inline sm:hidden">
                {t('common.pagination.pageOf', { page: safePage, totalPages })}
              </span>
            </>
          )}
        </span>
        {onPageSizeChange && (
          <label className="inline-flex items-center gap-2 text-sm text-gray-600">
            <span className="text-gray-500">{t('common.pagination.perPage')}</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              disabled={isLoading}
              data-test-id="pagination-page-size"
              className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className={cn('flex items-center gap-0 shrink-0', !compact && 'order-1 sm:order-2')} role="group">
        <button
          type="button"
          onClick={() => onPageChange(safePage - 1)}
          disabled={prevDisabled}
          className={cn(baseBtn, 'rounded-l-md')}
          aria-label={t('common.pagination.prev')}
          data-test-id="pagination-prev"
        >
          <ChevronLeft className="h-4 w-4" />
          {!compact && <span className="ml-1 hidden sm:inline">{t('common.pagination.prev')}</span>}
        </button>

        <div className="hidden sm:flex items-center">
          {items.map((item, idx) => {
            if (item === 'ellipsis-left' || item === 'ellipsis-right') {
              return (
                <span
                  key={`${item}-${idx}`}
                  className={cn(baseBtn, '-ml-px cursor-default hover:bg-white')}
                  aria-hidden="true"
                >
                  …
                </span>
              );
            }
            const isActive = item === safePage;
            const isSingleDisabled = totalPages <= 1;
            return (
              <button
                key={item}
                type="button"
                onClick={() => onPageChange(item)}
                disabled={isLoading || isSingleDisabled}
                aria-current={isActive ? 'page' : undefined}
                aria-label={t('common.pagination.goToPage', { page: item })}
                className={cn(
                  baseBtn,
                  '-ml-px',
                  isActive && 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700 z-10'
                )}
                data-test-id={`pagination-page-${item}`}
              >
                {item}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => onPageChange(safePage + 1)}
          disabled={nextDisabled}
          className={cn(baseBtn, 'rounded-r-md sm:-ml-px')}
          aria-label={t('common.pagination.next')}
          data-test-id="pagination-next"
        >
          {!compact && <span className="mr-1 hidden sm:inline">{t('common.pagination.next')}</span>}
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </nav>
  );
};
